import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TableCell } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AlertTriangle, ArrowDown, ArrowUp, Lock, Minus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/* Group-visible standings: rows outside the viewer's scope stay view-only. */
export const scopeLockToast = (message) => {
  toast("Details locked", {
    id: "scope-lock",
    description: message || "Complaint details for this location are visible to its own team and leadership.",
    icon: <Lock size={14} />,
  });
};

/* ---------- KPI Tile ---------- */
const deltaToneClass = (crimson, delta, deltaGoodWhenDown) => {
  if (crimson) return "text-white/90";
  const isBad = (delta > 0) === deltaGoodWhenDown;
  return isBad ? "text-[hsl(var(--status-red))]" : "text-[hsl(var(--status-green))]";
};

export const Tile = ({ label, value, sub, onClick, testId, variant, warn, warnIcon, delta, deltaGoodWhenDown = true, className }) => {
  const crimson = variant === "crimson";
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role="button"
      tabIndex={0}
      className={cn(
        "p-4 flex flex-col gap-1 cursor-pointer transition-all duration-150 hover:shadow-md hover:-translate-y-[1px]",
        crimson && "bg-[#BB162B] border-[#BB162B] text-white",
        !crimson && warn && "bg-[hsl(var(--status-amber)/0.06)] border-[hsl(var(--status-amber)/0.35)]",
        className
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={cn("text-[0.62rem] uppercase tracking-[0.12em] font-medium",
          crimson ? "text-white/80" : "text-muted-foreground")}>{label}</span>
        {warnIcon && <AlertTriangle size={13} className={crimson ? "text-white" : "text-[hsl(var(--status-amber))]"} />}
      </div>
      <div className="flex items-baseline gap-2">
        <span className={cn("kpi-value text-2xl sm:text-3xl", crimson ? "text-white" : "text-[hsl(var(--cx-ink))]")}>{value}</span>
        {delta !== undefined && delta !== null && delta !== 0 && (
          <span className={cn("flex items-center text-xs font-semibold tabular",
            deltaToneClass(crimson, delta, deltaGoodWhenDown))}>
            {delta > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {Math.abs(delta)}
          </span>
        )}
      </div>
      {sub && <div className={cn("text-xs", crimson ? "text-white/75" : "text-muted-foreground")}>{sub}</div>}
    </Card>
  );
};

export const PanelCard = ({ title, right, children, testId, className }) => (
  <Card data-testid={testId} className={className}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="font-serif text-base font-normal">{title}</CardTitle>
        {right}
      </div>
    </CardHeader>
    <CardContent className="pt-0 px-0">{children}</CardContent>
  </Card>
);

export const Movement = ({ movement }) => {
  if (movement === null || movement === undefined || movement === 0)
    return <Minus size={13} className="text-muted-foreground inline" aria-label="no change" />;
  return movement > 0 ? (
    <span className="inline-flex items-center text-[hsl(var(--status-green))] text-xs font-semibold tabular">
      <ArrowUp size={12} />{movement}
    </span>
  ) : (
    <span className="inline-flex items-center text-[hsl(var(--status-red))] text-xs font-semibold tabular">
      <ArrowDown size={12} />{Math.abs(movement)}
    </span>
  );
};

export const RankBadge = ({ rank }) => {
  if (rank > 3) return rank;
  return (
    <span className={cn("inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold",
      rank === 1 && "bg-[hsl(var(--cx-gold)/0.2)] text-[hsl(var(--cx-gold))]",
      rank === 2 && "bg-[hsl(var(--cx-slate)/0.15)] text-[hsl(var(--cx-slate))]",
      rank === 3 && "bg-[hsl(var(--status-amber)/0.15)] text-[hsl(var(--status-amber))]")}>
      {rank}
    </span>
  );
};

export const OutletCell = ({ o, myOutletId, locked }) => (
  <TableCell>
    <span className={cn("font-medium", locked && "text-muted-foreground")}>{o.outlet_name}</span>{" "}
    <span className="text-xs text-muted-foreground">{o.outlet_code}</span>
    {locked && <Lock size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground/70" aria-label="details locked" />}
    {o.outlet_id === myOutletId && <Badge variant="outline" className="ml-1.5 text-[0.6rem] border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]">You</Badge>}
  </TableCell>
);

export const DashboardSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-10 w-64" />
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={`dash-skeleton-${i}`} className="h-24" />)}
    </div>
    <Skeleton className="h-72" />
  </div>
);

export const PeriodToggle = ({ period, setPeriod }) => (
  <ToggleGroup
    type="single"
    value={period}
    onValueChange={(v) => v && setPeriod(v)}
    className="border rounded-md p-0.5 bg-[hsl(var(--cx-surface-2))]"
    data-testid="dashboard-period-toggle"
  >
    {[["today", "Today"], ["week", "This Week"], ["month", "This Month"]].map(([v, l]) => (
      <ToggleGroupItem key={v} value={v} data-testid={`period-toggle-${v}`}
        className="h-7 px-3 text-xs data-[state=on]:bg-[hsl(var(--cx-navy))] data-[state=on]:text-white rounded">
        {l}
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);
